import {
  transcriptState,
  isPremiumState,
  videoProgressState,
  settingsState,
  isPlayingState,
} from '@/store'
import { Play, Pause, HelpCircle } from 'react-feather'
import { useState, useEffect } from 'react'
import { useRef } from 'react'
import { useRecoilState } from 'recoil'
import api from '@/utils/api'
import analytics from '@/utils/analytics'
import { getYouTubeID, hideAllTooltips } from '@/utils/index'
import toast from 'react-hot-toast'
import Button from '@/components/Button'
import ReactTooltip from 'react-tooltip'
import { useShowAutotranscriptModal } from '@/components/Modal'

export default function AutoTranscriptButton({
  content,
  setNewContent,
  videoUrl,
  isDemo,
  className,
}) {
  const [isWorking, setIsWorking] = useState(false)
  const [isLoading, setIsloading] = useState(false)
  const [transcript, setTranscript] = useRecoilState(transcriptState)
  const [isPremium] = useRecoilState(isPremiumState)
  const [progress] = useRecoilState(videoProgressState)
  const [settings] = useRecoilState(settingsState)
  const [, setIsPlaying] = useRecoilState(isPlayingState)
  const showAutotranscriptModal = useShowAutotranscriptModal()
  const transcriptIdsRef = useRef([])

  useEffect(() => {
    if (!isWorking || !transcript) {
      return
    }

    const currentProgress = progress.playedSeconds || 0
    const { start, text } = getCurrentTranscript({ currentProgress })

    if (!text) {
      return
    }

    copyToEditor({ text, start })
  }, [progress, content])

  async function loadTranscript() {
    if (transcript) {
      return transcript
    }

    const language = (settings && settings.transcriptLanguage) || 'en'
    const ytVideoID = getYouTubeID(videoUrl)
    const response = await api.get(`/transcript/${ytVideoID}?lang=${language}`)
    const loadedTranscript = response.data
    setTranscript(loadedTranscript)
    return loadedTranscript
  }

  async function onTranscriptClick() {
    hideAllTooltips()

    if (!isPremium && !isDemo) {
      analytics.track('(Auto Transcript Button) Not Premium')
      showAutotranscriptModal()
      return
    }

    try {
      setIsloading(true)
      analytics.track('Auto Transcript button clicked')
      const loadedTranscript = await loadTranscript()

      if (loadedTranscript.length === 0) {
        toast.error(
          "We couldn't generate transcripts for this video or language"
        )
        setIsloading(false)
        return
      }
      setIsloading(false)

      const newValue = !isWorking

      setIsPlaying(newValue)
      setIsWorking(newValue)
    } catch (error) {
      toast.error("We couldn't generate transcripts for this video or language")
      analytics.track('Error loading transcript')
      setTranscript([])
    } finally {
      setIsloading(false)
    }
  }

  function getCurrentTranscript({ currentProgress }) {
    for (let index = 0; index < transcript.length; index++) {
      const item = transcript[index]
      const start = +item.start
      const nextStart = transcript[index + 1] && +transcript[index + 1].start

      // Cached transcripts have ._id :(
      const notAlreadyFound = !transcriptIdsRef.current.includes(
        item.id || item._id
      )

      if (
        currentProgress >= start &&
        !(currentProgress >= nextStart) &&
        notAlreadyFound
      ) {
        const found = {
          ...item,
          index,
        }

        transcriptIdsRef.current = [
          ...transcriptIdsRef.current,
          found.id || found._id,
        ]
        return found
      }
    }

    return {}
  }

  function copyToEditor({ text, start }) {
    const newContent = `${content} ${text}`
    setNewContent({ text: newContent, start, pauseOnType: false })
    const textarea = document.querySelector('.editor-content-wrapper textarea')
    if (textarea) {
      setTimeout(() => (textarea.scrollTop = textarea.scrollHeight), 50)
    }
  }

  return (
    <>
      <Button
        data-tip
        data-for="autotranscript-button"
        isLoading={isLoading}
        onClick={onTranscriptClick}
        className={className}
        tabIndex={-1}
      >
        {isWorking ? (
          <Pause className="h-5 w-5" />
        ) : (
          <Play className="h-5 w-5" />
        )}
      </Button>

      <ReactTooltip
        place="top"
        effect="solid"
        id="autotranscript-button"
        clickable
        delayHide={100}
      >
        <span className="center">
          {`Autotranscript${isPremium ? '' : ' (Premium)'}`}
          <HelpCircle
            onClick={() => {
              analytics.track('(Autotranscript) tooltip info clicked')
              showAutotranscriptModal()
            }}
            className="clickable-opacity ml-2 h-5 w-5"
          />
        </span>
      </ReactTooltip>
    </>
  )
}
